import { List } from 'immutable';
import { DateTime } from 'luxon';
import type { AnyAction } from 'redux-saga';
import { put, select, take, takeEvery } from 'redux-saga/effects';

import { tracker } from 'app-components/utils/core';

import { accountsActions, accountsTypes } from '../accounts';
import type { RealEstateAccountProps } from '../accounts/accountsTypes';
import { resourceSagaUtils, resourceStoreTypes } from '../core';
import type { CoreAction, ResourceActionCreator, ResourceFailureActionCreator } from '../core/resourceStoreTypes';
import type { HousingSelectionType } from '../housing-selection/housingSelectionTypes';
import { institutionLoginsActions, institutionLoginsTypes } from '../institution-logins';
import { transactionsActions, transactionsTypes } from '../transactions';
import * as actions from './houseStatsActions';
import { getHouse, getZillowFIId } from './houseStatsSelectors';
import type { ZillowSubmittingObject } from './houseStatsTypes';

const mkQcsSyncGetHouseStatsNewConfig =
  resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<HousingSelectionType>();

const mkGetStatsConfig = (
  successAction: ResourceActionCreator<HousingSelectionType>,
  failureAction: ResourceFailureActionCreator,
) =>
  mkQcsSyncGetHouseStatsNewConfig({
    resourceName: 'realestate',
    resourceBaseUrl: (action) => `realestate/assets/provider/${action.payload}`,
    successAction,
    failureAction,
  });

export function* getHouseStatsActionWatcher() {
  const resourceConfig = mkGetStatsConfig(actions.getHouseDataSuccess, actions.getHouseDataFailure);
  yield takeEvery(
    actions.getHouseData.type,
    resourceSagaUtils.qcsSyncGetResource<HousingSelectionType>,
    resourceConfig,
  );
}

export function* createZillowAccountActionWatcher() {
  yield takeEvery(
    actions.createZillowAccount.type,
    function* _createZillowAccount(createAccountAction: {
      meta: any;
      payload: ZillowSubmittingObject;
      type: 'CREATE_ZILLOW_ACCOUNT';
    }) {
      const { manualAccount, scope, accountNameQCS, amount, link, businessId } = createAccountAction.payload;
      tracker.track(tracker.events.zillowUX_5, {
        userAction: 'confirm',
        isManual: manualAccount,
      });
      let instLogInId: string;
      let accountParams: Partial<RealEstateAccountProps>;
      const zillowFIId: string = yield select(getZillowFIId);

      if (!manualAccount) {
        if (!zillowFIId) {
          const institutionLoginToUpsert = institutionLoginsTypes.mkInstitutionLoginClient({
            clientId: scope,
            cpSetupMode: 'SKIP_ACCOUNT_DISCOVERY',
            channel: 'ZILLOW',
            name: 'Zillow',
          });
          yield put(
            institutionLoginsActions.addInstitutionLoginAndAccounts(
              { institutionLogin: institutionLoginToUpsert, accounts: [] },
              { scope },
            ),
          );
          const instLoginIdResponse: CoreAction<any, any> = yield take(
            institutionLoginsActions.addInstitutionLoginAndAccountsResponse,
          );
          instLogInId = instLoginIdResponse.payload.institutionLogin.id;
        } else {
          instLogInId = zillowFIId;
        }
        const address: HousingSelectionType = yield select(getHouse);
        tracker.track(tracker.events.zillowUX_5, {
          accountNameEdited: address.line1 !== accountNameQCS,
          type: businessId ? 'BUSINESS' : 'PERSONAL',
          link: link !== '0',
        });
        const aggregators = List([
          accountsTypes.mkAccountAggregator({
            channel: 'ZILLOW',
            cpId: address?.assetDetailsList ? address.assetDetailsList[0].thirdPartyPropertyID : '',
          }),
        ]);
        accountParams = {
          id: link === '0' ? undefined : link,
          clientId: scope,
          institutionLoginId: instLogInId,
          name: link === '0' ? accountNameQCS.trim() : undefined,
          line1: address.line1,
          line2: address.line2,
          city: address.city,
          state: address.state,
          zipCode: address.zipCode,
          type: 'REAL_ESTATE',
          aggregators,
          businessId: businessId,
          usageType: businessId ? 'BUSINESS' : 'PERSONAL',
        };
      } else {
        // manual accounts
        accountParams = {
          clientId: scope,
          institutionLoginId: undefined,
          isExcludedFromAccountBar: false,
          isExcludedFromBudgets: true,
          isExcludedFromReports: true,
          name: accountNameQCS.trim(),
          type: 'REAL_ESTATE',
          businessId: businessId,
          usageType: businessId ? 'BUSINESS' : 'PERSONAL',
        };
      }
      let accountId: string | undefined | null = '0';
      let institutionLoginId = '0';
      if (link === '0') {
        // manual account creation and non linked accounts
        const accountToUpsert = accountsTypes.mkAccount('REAL_ESTATE', accountParams);
        yield put(accountsActions.createAccount(accountToUpsert));
        const accountCreationResponse: AnyAction = yield take(accountsActions.createAccountSuccess);
        const respAccount: accountsTypes.RealEstateAccount = accountCreationResponse?.payload;
        accountId = respAccount?.id;
        institutionLoginId = respAccount.institutionLoginId || '0';
      } else if (!manualAccount) {
        // link to existing  account
        const accountToUpsert = accountsTypes.mkUpdateAccount('REAL_ESTATE', accountParams);
        yield put(accountsActions.updateAccount(accountToUpsert));
        const accountUpdateResponse: AnyAction = yield take(accountsActions.updateAccountSuccess);
        const respAccount: accountsTypes.RealEstateAccount = accountUpdateResponse?.payload;
        accountId = link;
        institutionLoginId = respAccount.institutionLoginId || '0';
      }
      if (manualAccount) {
        const openingBalanceAsOf = (amount && Number(amount.toFixed(2))) || 0.0;
        const openingBalanceAsOfOn = DateTime.local().toISODate() || '1970-01-01';

        if (openingBalanceAsOf !== 0.0 && accountId) {
          const newTxn = new transactionsTypes.CashFlowTransaction({
            accountId,
            payee: `Zestimate ${accountNameQCS.trim()}`,
            coa: {
              type: 'BALANCE_ADJUSTMENT',
              id: '0',
            },
            state: 'CLEARED',
            isReviewed: true,
            amount: openingBalanceAsOf,
            postedOn: openingBalanceAsOfOn,
            clientId: scope,
            isExcludedFromReports: true,
            isExcludedFromF2S: true,
            isBill: false,
            isSubscription: false,
          });
          yield put(transactionsActions.createTransaction(newTxn, { context: 'addAccounts' }));
        }
      } else if (institutionLoginId) {
        yield put(institutionLoginsActions.refreshAccounts({ institutionLoginId }));
      }
    },
  );
}

// ====================================================
// EXPORTS

export default [getHouseStatsActionWatcher, createZillowAccountActionWatcher];
